import { FC } from 'react';
import { Visibility } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import { useOne } from '@refinedev/core';
import { DeleteButton } from '@refinedev/mui';
import { useDrag, useDrop } from 'react-dnd';

import { UnifiedMitigation } from '../../../../../types';

interface UnifiedMitigationItemProps {
  id: string;
  scenarioUnifiedMitigation?: string;
  moveMitigation: (id: string, to: number, mutate?: boolean) => void;
  findMitigation: (id: string) => { index: number };
  openMitigationDialog: (mitigation: UnifiedMitigation) => void;
  onDeletedMitigation: (scenarioUnifiedMitigaion: string) => void;
  dropAreaName: string;
}

const UnifiedMitigationItem: FC<UnifiedMitigationItemProps> = ({
  id,
  scenarioUnifiedMitigation,
  moveMitigation,
  findMitigation,
  openMitigationDialog,
  onDeletedMitigation,
  dropAreaName
}) => {
  const {
    result: unifiedMitigation,
    query: { isLoading }
  } = useOne<UnifiedMitigation>({
    resource: 'unified_mitigations',
    id
  });

  const originalIndex = findMitigation(id).index;

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: dropAreaName,
      item: { id, originalIndex },
      collect: monitor => ({
        isDragging: monitor.isDragging()
      }),
      end: (item, monitor) => {
        const { id: droppedId, originalIndex } = item;
        const { index: overIndex } = findMitigation(droppedId);
        const didDrop = monitor.didDrop();

        if (!didDrop) {
          moveMitigation(droppedId, originalIndex);
        } else {
          moveMitigation(droppedId, overIndex, true);
        }
      }
    }),
    [id, moveMitigation, originalIndex]
  );

  const [, drop] = useDrop(
    () => ({
      accept: dropAreaName,
      hover: ({ id: draggedId }: { id: string }) => {
        if (draggedId !== id) {
          const { index: overIndex } = findMitigation(id);
          moveMitigation(draggedId, overIndex);
        }
      }
    }),
    [findMitigation, moveMitigation]
  );

  const opacity = isDragging ? 0 : 1;

  return (
    <Box
      key={id}
      component="li"
      ref={(node: HTMLLIElement | null) => drag(drop(node))}
      sx={{
        border: '1px dashed gray',
        padding: '0.5rem 1rem',
        marginBottom: '.5rem',
        backgroundColor: 'background.paper',
        cursor: 'move',
        opacity
      }}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body1">{unifiedMitigation?.name}</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton size="small" onClick={() => unifiedMitigation && openMitigationDialog(unifiedMitigation)}>
              <Visibility />
            </IconButton>
            <DeleteButton
              hideText
              recordItemId={scenarioUnifiedMitigation}
              resource="scenario_unified_mitigations"
              confirmTitle={'Do you want to delete Mitigation: "' + unifiedMitigation?.name + '" ?'}
              onSuccess={() => {
                if (scenarioUnifiedMitigation) onDeletedMitigation(scenarioUnifiedMitigation);
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UnifiedMitigationItem;
